import React from 'react';
import { AlertCircle, CheckCircle2, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '../index';

const variants = {
  info: {
    wrap: 'bg-[color-mix(in_srgb,var(--info)_12%,transparent)] border-[color-mix(in_srgb,var(--info)_30%,transparent)] text-[var(--info)]',
    Icon: Info,
  },
  success: {
    wrap: 'bg-[color-mix(in_srgb,var(--success)_12%,transparent)] border-[color-mix(in_srgb,var(--success)_30%,transparent)] text-[var(--success)]',
    Icon: CheckCircle2,
  },
  warning: {
    wrap: 'bg-[color-mix(in_srgb,var(--warning)_12%,transparent)] border-[color-mix(in_srgb,var(--warning)_30%,transparent)] text-[var(--warning)]',
    Icon: AlertTriangle,
  },
  danger: {
    wrap: 'bg-[color-mix(in_srgb,var(--destructive)_12%,transparent)] border-[color-mix(in_srgb,var(--destructive)_30%,transparent)] text-[var(--destructive)]',
    Icon: AlertCircle,
  },
} as const;

export interface AlertProps {
  variant?: keyof typeof variants;
  title?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  variant = 'info', title, children, onClose, className,
}) => {
  const { wrap, Icon } = variants[variant];
  return (
    <div
      role="alert"
      className={cn('flex gap-3 rounded-lg border px-3 py-2.5 text-sm', wrap, className)}
    >
      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-[var(--fg)]">
        {title && <p className="font-medium text-sm mb-0.5">{title}</p>}
        {children && <div className="text-sm text-[var(--fg-muted)]">{children}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 rounded p-0.5 hover:bg-[var(--muted)] text-[var(--fg-muted)]"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
